import { ApiError } from "../api/client";
import { Icon } from "../ui/Icon";
import { Banner, EmptyState, type Art } from "../ui/kit";

/** Скелет, а не крутилка: показывает, ЧТО грузится, и экран не прыгает,
 *  когда данные приехали. Строки — по форме списка: метка, текст, время. */
export function Loading({ rows = 3, label }: { rows?: number; label: string }) {
  return (
    <div className="stack stack--tight" role="status" aria-busy="true" aria-label={label}>
      <span className="skel skel--title" />
      {Array.from({ length: rows }).map((_, i) => (
        <div className="skel-row" key={i}>
          <span className="skel skel--line" />
          <span className={`skel skel--line ${i % 3 === 2 ? "skel--w60" : "skel--w80"}`} />
          <span className="skel skel--line skel--w40" />
        </div>
      ))}
    </div>
  );
}

/** Ошибка блока: что случилось и что сделать, голосом интерфейса.
 *  Технический текст сюда не попадает: бэкенд отдаёт человеческое сообщение,
 *  подробности остаются в его логе. Заголовок экрана остаётся на месте. */
export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const api = error instanceof ApiError ? error : null;
  const message = api?.message ?? "Не удалось загрузить данные.";
  const hint =
    api?.code === "NETWORK"
      ? "Нет связи с кабинетом. Проверьте интернет и повторите."
      : api?.isNotImplemented
        ? "Этот раздел ещё не готов."
        : null;

  return (
    <div className="err" role="alert">
      <span className="err__title">
        <Icon name="alert" />
        {message}
      </span>
      {hint && <p>{hint}</p>}
      {onRetry && (
        <button type="button" className="btn btn--sm" onClick={onRetry}>
          <Icon name="refresh" small />
          Повторить
        </button>
      )}
    </div>
  );
}

/** Ошибка действия (сохранить, удалить, сменить) — плашкой рядом с кнопкой,
 *  а не тостом: тост уезжает, а ошибку надо прочитать. Берёт ошибку прямо
 *  из useMutation; «Скрыть» сбрасывает её (`reset`). Нет ошибки — ничего. */
export function ActionError({
  mutation,
  fallback,
}: {
  mutation: { error: unknown; reset: () => void };
  fallback: string;
}) {
  if (!mutation.error) return null;
  const message = mutation.error instanceof Error && mutation.error.message ? mutation.error.message : fallback;
  return (
    <Banner tone="danger">
      <span>{message}</span>
      <span>
        <button type="button" className="btn btn--sm" onClick={mutation.reset}>
          Скрыть
        </button>
      </span>
    </Banner>
  );
}

/** Пустой экран — приглашение к действию, а не сообщение о поломке.
 *  Говорит, что здесь появится и как это сюда положить. */
export function Empty({
  title,
  text,
  action,
  art,
  flat,
}: {
  title: string;
  text: string;
  action?: React.ReactNode;
  art?: Art;
  flat?: boolean;
}) {
  return <EmptyState art={art} title={title} text={text} action={action} flat={flat} />;
}
